import PropTypes from 'prop-types';
import { useState, useCallback, useEffect } from 'react';
// @mui
import ListItemText from '@mui/material/ListItemText';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
// hooks
import { useResponsive } from '../../../hooks/use-responsive';
// routes
import { useRouter, usePathname } from '../../../routes/hooks';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { setCurShopId } from 'src/redux/slices/startform';
import { getShopsList } from '../../../redux/slices/shops';
// components
import Iconify from '../../../components/iconify';
import ShopSwitchDialog from '../../../components/shop-select/ShopSwitchDialog';

// ----------------------------------------------------------------------

export default function ShopSwitcher({ sx, ...other }) {
    const { current_shop: shops } = useSelector((state) => state.shops);
    const dispatch = useDispatch();
    const pathname = usePathname();
    const router = useRouter();

    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!shops.length) {
            dispatch(getShopsList());
        }
    }, [dispatch, shops.length]);

    // Extract current shopId from URL
    const pathParts = pathname.split('/');
    const currentShopId = pathParts[2]; // Index 2 should be shopId

    const currentShop = shops.find((shop) => String(shop.id) === String(currentShopId));

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChangeShop = useCallback(
        (newShopId) => {
            handleClose();
            // immediately store selected shopId in redux so all components get it right away
            try {
                dispatch(setCurShopId(newShopId));
            } catch (e) {}
            const newPath = pathname.replace(`/${currentShopId}`, `/${newShopId}`);
            router.push(newPath);
        },
        [pathname, router, currentShopId, dispatch]
    );

    return (
        <>
            <ButtonBase
                onClick={handleOpen}
                sx={{
                    pl: 2,
                    pr: 1,
                    py: 1.5,
                    width: 1,
                    borderRadius: 1.5,
                    bgcolor: 'background.neutral',
                    ...sx,
                }}
                {...other}
            >
                <Avatar
                    alt={currentShop?.name}
                    src={currentShop?.avatar || ''}
                    variant="rounded"
                    sx={{ width: 28, height: 28, mr: 1.5 }}
                >
                    {currentShop?.name?.charAt(0).toUpperCase()}
                </Avatar>

                <ListItemText
                    primary={currentShop?.name || 'Select Shop'}
                    secondary={currentShop?.category || 'Category'}
                    primaryTypographyProps={{ typography: 'subtitle2', noWrap: true }}
                    secondaryTypographyProps={{ typography: 'caption', noWrap: true, sx: { color: 'text.secondary' } }}
                />

                <Iconify icon="eva:chevron-down-fill" sx={{ ml: 1, color: 'text.disabled' }} />
            </ButtonBase>

            <ShopSwitchDialog
                open={open}
                onClose={handleClose}
                shops={shops}
                currentShopId={currentShopId}
                onChangeShop={handleChangeShop}
            />
        </>
    );
}

ShopSwitcher.propTypes = {
    sx: PropTypes.object,
};
